import { useQuery, useMutation } from '@tanstack/react-query';
import { useAPI } from '@pella/cosmos-react';
import { toast } from '@pella/sash-framework';
import { QUERY_KEYS } from '../../utils/enums';
import { getInstanceName } from '../../data/configure';
import { ApiResponseHandler } from '../../utils/common';

// The production hostname branch cannot be covered in a jsdom test environment.
/* istanbul ignore next */
const leanTechApiUrl =
  window.location.hostname === 'equipment-action-portal.pella.com'
    ? 'https://thriveapi.pella.com'
    : 'https://thrivetestapi.pella.com';

/* istanbul ignore next */
const leanTechFetch = async (url, options = {}) => {
  const instanceName = getInstanceName();
  const headers = {
    'Content-Type': 'application/json',
    scopeID:
      instanceName === 'EBSPROD' ? '952a653a-8152-43e3-8bde-1f5bd991630c' : '3809414a-644a-4f20-b073-c80a1b4c7b5d',
    tenantID:
      instanceName === 'EBSPROD' ? '1F773DFE-0668-49F6-B8B0-E711CF4BDC2B' : '2AE38024-DCFD-41EA-B76E-54C8D41D3C22',
  };
  const response = await fetch(url, { headers, ...options });
  const data = await response.json();
  return { status: response.status, data };
};

/**
 * @typedef {object} SparePartsParams
 * @property {string} equipnumber - Equipment number to fetch spare parts for
 * @property {string | null} [partnum] - Optional part number filter
 * @property {string | null} [projNum] - Optional project number filter
 * @property {string | null} [sparecode] - Optional spare code filter
 */

/**
 * @typedef {object} ProjectsParams
 * @property {string} [assignedTo] - Employee ID the project is assigned to
 * @property {string} [projNum] - Project number
 * @property {string} [projid] - Project ID
 */

/**
 * @typedef {object} AddAssignmentParams
 * @property {string} projectId - The project ID to add the assignment to
 * @property {string} projNum - The project number
 */

/**
 * Hook to fetch a single piece of equipment by equipment number from the LeanTech API.
 * @param {string | null | undefined} equipNum - The equipment number; when falsy, the query is disabled
 * @returns {object} Query result with equipment data
 */
export const useEquipmentByNumber = (equipNum) => {
  const { data, isLoading, isSuccess, isError, error } = useQuery({
    queryKey: [QUERY_KEYS.THRIVE_EQUIPMENT_BY_NUMBER, equipNum],
    queryFn: async () => {
      const { data: json } = await leanTechFetch(`${leanTechApiUrl}/api/CSTM_Equipment/EquipNum/${equipNum}`);
      const result = ApiResponseHandler({ data: json });
      if (!result) throw new Error('Equipment not found');
      return result;
    },
    enabled: Boolean(equipNum),
    staleTime: Number.POSITIVE_INFINITY,
  });

  return {
    equipment: data,
    equipmentIsLoading: isLoading,
    equipmentIsSuccess: isSuccess,
    equipmentIsError: isError,
    equipmentError: error,
  };
};

/**
 * Hook providing a mutation to start a LeanTech downtime event.
 * @returns {{startDowntimeEvent: (event: object) => Promise<object>, isStartingDowntimeEvent: boolean}}
 */
export const useStartDowntimeEvent = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (event) => {
      try {
        const response = await leanTechFetch(`${leanTechApiUrl}/api/CSTM_StartDowntimeEvent`, {
          method: 'POST',
          body: JSON.stringify(event),
        });
        if (response.status === 201 && response.data) {
          return {
            ...response,
            data: {
              nextTech: response.data.NextTech,
              id: response.data.ID,
              projNum: response.data.PROJNUM,
              projTypeId: response.data.PROJTYPEID,
              taskTypeId: response.data.TASKTYPEID,
              requestedBy: response.data.REQUESTEDBY,
              requestedByFN: response.data.REQUESTEDBYFN,
              requestedByLN: response.data.REQUESTEDBYLN,
              siteId: response.data.SITEID,
              description: response.data.DESCRIPTION,
              comments: response.data.COMMENTS,
              dateRequest: response.data.DATEREQUEST,
              timeRequest: response.data.TIMEREQUEST,
              dateDue: response.data.DATEDUE,
              priority: response.data.PRIORITY,
              status: response.data.STATUS,
              unplanned: response.data.UNPLANNED,
              approvedBy: response.data.APPROVEDBY,
              appByFirstName: response.data.APPBYFIRSTNAME,
              appByLastName: response.data.APPBYLASTNAME,
              isRequest: response.data.ISREQUEST,
              deleted: response.data.DELETED,
              pctComplete: response.data.PCTCOMPLETE,
              pickList06: response.data.PickList06,
              isDowntime: response.data.IsDowntime,
              dateRequestUTC: response.data.DateRequestUTC,
            },
          };
        }
        return response;
      } catch (error) {
        return error.response;
      }
    },
  });

  return { startDowntimeEvent: mutateAsync, isStartingDowntimeEvent: isPending };
};

/**
 * Custom hook for all thrive/v3 API interactions.
 *
 * Covers the following endpoints:
 *  - GET  /thrive/v3/spare-parts
 *  - GET  /thrive/v3/projects
 *  - GET  /thrive/v3/sites/:siteId/equipment
 *  - GET  /thrive/v3/employees/:employeeId/user-defaults
 *  - GET  /thrive/v3/employees/:employeeId/user-status
 *  - POST /thrive/v3/assignments
 */
export const useThrive = () => {
  const { api, apiReady } = useAPI();

  /**
   * Fetch spare parts for a given work order / equipment
   * @param {SparePartsParams} params
   * @returns {object} Query result with spare parts list
   */
  // @ts-ignore
  const useSpareParts = ({ equipnumber, partnum = null, projNum = null, sparecode = null } = {}) => {
    const { data, isLoading, isSuccess, isError, error } = useQuery({
      queryKey: [QUERY_KEYS.THRIVE_SPARE_PARTS, equipnumber, partnum, projNum, sparecode],
      // @ts-ignore
      queryFn: () => api.thrive.getSpareParts({ equipnumber, partnum, projNum, sparecode }),
      enabled: apiReady && Boolean(equipnumber),
      staleTime: Number.POSITIVE_INFINITY,
    });

    return {
      spareParts: data,
      sparePartsIsLoading: isLoading,
      sparePartsIsSuccess: isSuccess,
      sparePartsIsError: isError,
      sparePartsError: error,
    };
  };

  /**
   * Fetch a list of projects, optionally filtered by assignment or project number
   * @param {ProjectsParams} params
   * @returns {object} Query result with projects list
   */
  const useProjects = ({ assignedTo, projNum, projid } = {}) => {
    const enabled = apiReady && (Boolean(assignedTo) || Boolean(projNum) || Boolean(projid));

    const { data, isLoading, isSuccess, isError, error } = useQuery({
      queryKey: [QUERY_KEYS.THRIVE_PROJECTS, assignedTo, projNum, projid],
      // @ts-ignore
      queryFn: () => api.thrive.getProjects({ assignedTo, projNum, projid }),
      enabled,
      staleTime: Number.POSITIVE_INFINITY,
    });

    return {
      projects: data,
      projectsIsLoading: isLoading,
      projectsIsSuccess: isSuccess,
      projectsIsError: isError,
      projectsError: error,
    };
  };

  /**
   * Fetch all equipment for a given site
   * @param {string} siteId - The site ID
   * @returns {object} Query result with equipment list
   */
  const useEquipment = (siteId) => {
    const { data, isLoading, isSuccess, isError, error } = useQuery({
      queryKey: [QUERY_KEYS.THRIVE_EQUIPMENT, siteId],
      // @ts-ignore
      queryFn: () => api.thrive.getEquipment({ siteId }),
      enabled: apiReady && Boolean(siteId),
      staleTime: Number.POSITIVE_INFINITY,
    });

    return {
      equipment: data,
      equipmentIsLoading: isLoading,
      equipmentIsSuccess: isSuccess,
      equipmentIsError: isError,
      equipmentError: error,
    };
  };

  /**
   * Fetch user default preferences for an employee (site, department, display name)
   * @param {string} employeeId - The employee user ID
   * @returns {object} Query result with user defaults
   */
  const useEmployeeUserDefaults = (employeeId) => {
    const { data, isLoading, isSuccess, isError, error } = useQuery({
      queryKey: [QUERY_KEYS.THRIVE_EMPLOYEE_USER_DEFAULTS, employeeId],
      // @ts-ignore
      queryFn: () => api.thrive.getEmployeeUserDefaults({ employeeId }),
      enabled: apiReady && Boolean(employeeId),
      staleTime: Number.POSITIVE_INFINITY,
    });

    return {
      userDefaults: data,
      userDefaultsIsLoading: isLoading,
      userDefaultsIsSuccess: isSuccess,
      userDefaultsIsError: isError,
      userDefaultsError: error,
    };
  };

  /**
   * Fetch the current user status for an employee
   * @param {string} employeeId - The employee user ID
   * @returns {object} Query result with user status
   */
  const useEmployeeUserStatus = (employeeId) => {
    const { data, isLoading, isSuccess, isError, error } = useQuery({
      queryKey: [QUERY_KEYS.THRIVE_EMPLOYEE_USER_STATUS, employeeId],
      // @ts-ignore
      queryFn: () => api.thrive.getEmployeeUserStatus({ employeeId }),
      enabled: apiReady && Boolean(employeeId),
      staleTime: Number.POSITIVE_INFINITY,
    });

    return {
      userStatus: data,
      userStatusIsLoading: isLoading,
      userStatusIsSuccess: isSuccess,
      userStatusIsError: isError,
      userStatusError: error,
    };
  };

  /**
   * Mutation to add a project assignment for the current user.
   * Shows a toast on success or failure.
   * @returns {object}
   */
  const useAddAssignment = () =>
    useMutation({
      // @ts-ignore
      mutationFn: (/** @type {AddAssignmentParams} */ params) => api.thrive.addProjectAssignment(params),
      onSuccess: () => {
        toast.success('Assignment added successfully.');
      },
      onError: (error) => {
        toast.error(`Failed to add assignment: ${error.message}`);
      },
    });

  return {
    useSpareParts,
    useProjects,
    useEquipment,
    useEquipmentByNumber,
    useStartDowntimeEvent,
    useEmployeeUserDefaults,
    useEmployeeUserStatus,
    useAddAssignment,
  };
};
